<template>
  <!--
    StepGrundlag.vue - Step 1: Grundlag (NY STRUKTUR)
    =================================================
    Samlet upload-område for ALLE dokumenttyper.

    Features:
    - Én drop-zone for alle dokumenter
    - AI kategoriserer automatisk (live mode) eller manuelt (test mode)
    - Drag-and-drop mellem kategorier
    - Klik på dokument åbner modal med preview + felter
    - Confidence badges fra AI

    Kategorier:
    - Andre dokumenter (startpunkt, altid synlig)
    - Grundlag / Fakturaer (mindst 1 påkrævet)
    - Rykkere
    - Kreditnotaer
  -->
  <div class="step-grundlag">
    <!-- Header -->
    <div class="step-header mb-2 text-center">
      <h4 class="mb-0">
        Upload dokumenter
      </h4>
    </div>

    <!-- AI / Manuel toggle switch -->
    <div class="mode-switch-wrapper mb-1">
      <div
        class="mode-switch"
        :class="{ 'mode-ai-on': isLiveMode }"
      >
        <div class="mode-switch-track">
          <div
            class="mode-switch-thumb"
            :class="{ 'thumb-right': !isLiveMode }"
          />
        </div>
        <button
          type="button"
          class="mode-switch-option"
          :class="{ active: isLiveMode, 'option-disabled': claudeUnavailable }"
          :disabled="claudeUnavailable"
          :title="claudeUnavailable ? $t('DEBTIA_CREATE_DOC_AI_UNAVAILABLE') : null"
          @click="isLiveMode = true"
        >
          <feather-icon
            icon="ZapIcon"
            size="15"
          />
          <span>AI aktiv</span>
        </button>
        <button
          type="button"
          class="mode-switch-option"
          :class="{ active: !isLiveMode }"
          @click="isLiveMode = false"
        >
          <feather-icon
            icon="Edit3Icon"
            size="15"
          />
          <span>Manuel</span>
        </button>
      </div>
    </div>

    <!-- Info boks (skifter tekst baseret på mode) -->
    <div class="ai-info-hint-wrapper mb-1">
      <div
        class="ai-info-hint"
        :class="{ 'ai-info-hint-manual': !isLiveMode }"
      >
        <feather-icon
          :icon="isLiveMode ? 'InfoIcon' : 'Edit3Icon'"
          size="15"
          class="ai-info-icon"
        />
        <span v-if="claudeUnavailable">{{ $t('DEBTIA_CREATE_DOC_AI_UNAVAILABLE') }}</span>
        <span v-else-if="isLiveMode">AI kategoriserer, læser og udfylder automatisk data om sagen og debitor.</span>
        <span v-else>Upload dokumenter om sagen og debitor og udfyld data manuelt.</span>
      </div>
    </div>

    <!-- Upload Drop Zone -->
    <div
      class="main-drop-zone"
      :class="{ 'drop-zone-active': isDragging }"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="triggerFileInput"
    >
      <div class="drop-zone-content">
        <feather-icon
          icon="UploadCloudIcon"
          size="40"
          class="text-primary mb-75"
        />
        <p class="drop-zone-title mb-25">
          Træk filer hertil eller klik for at vælge
        </p>
        <small class="text-muted">
          PDF, DOC, DOCX, JPG, PNG
        </small>
      </div>

      <!-- Skjult file input -->
      <input
        ref="fileInput"
        type="file"
        :accept="acceptedFileTypes"
        multiple
        class="d-none"
        @change="handleFileSelect"
      >
    </div>

    <!-- spacer -->

    <!-- AI Analyse Progress -->
    <div
      v-if="state.aiAnalysis.isAnalyzing"
      class="ai-progress-section mb-2"
    >
      <div class="d-flex justify-content-between align-items-center mb-50">
        <span>
          <feather-icon
            icon="CpuIcon"
            class="mr-50 text-primary"
          />
          {{ $t('DEBTIA_CREATE_GRUNDLAG_AI_ANALYZING') }}
        </span>
        <span class="text-muted">
          {{ state.aiAnalysis.completedCount }}/{{ state.aiAnalysis.totalCount }}
        </span>
      </div>
      <b-progress
        :value="state.aiAnalysis.progress"
        max="100"
        variant="primary"
        animated
      />
      <small
        v-if="state.aiAnalysis.currentDocument"
        class="text-muted"
      >
        {{ $t('DEBTIA_CREATE_GRUNDLAG_PROCESSING') }}: {{ state.aiAnalysis.currentDocument }}
      </small>
    </div>

    <!-- Dokumenter Grid (kun synlig når der er uploadet filer) -->
    <div
      v-if="hasAnyDocuments"
      class="documents-grid"
    >
      <!-- Andre dokumenter (startpunkt) -->
      <DocumentCategory
        category="oevrig"
        :title="$t('DEBTIA_CREATE_GRUNDLAG_CAT_OTHER')"
        icon="InboxIcon"
        variant="secondary"
        :documents="state.documents.oevrig"
        :is-test-mode="!isLiveMode"
        :always-show="true"
        @analyze="handleAnalyzeDocument"
        @move="handleMoveDocument"
        @remove="handleRemoveDocument"
        @edit="handleEditDocument"
      />

      <!-- Grundlag / Fakturaer (påkrævet) - skjult i AI mode hvis tom -->
      <transition name="category-fade">
        <DocumentCategory
          v-if="!isLiveMode || state.documents.grundlag.length > 0"
          category="grundlag"
          :title="$t('DEBTIA_BASIS_INVOICES')"
          icon="FileTextIcon"
          variant="primary"
          :documents="state.documents.grundlag"
          :required="true"
          :is-test-mode="!isLiveMode"
          @analyze="handleAnalyzeDocument"
          @move="handleMoveDocument"
          @remove="handleRemoveDocument"
          @edit="handleEditDocument"
        />
      </transition>

      <!-- Rykkere - skjult i AI mode hvis tom -->
      <transition name="category-fade">
        <DocumentCategory
          v-if="!isLiveMode || state.documents.rykkere.length > 0"
          category="rykkere"
          :title="$t('DEBTIA_REMINDERS')"
          icon="AlertCircleIcon"
          variant="warning"
          :documents="state.documents.rykkere"
          :is-test-mode="!isLiveMode"
          @analyze="handleAnalyzeDocument"
          @move="handleMoveDocument"
          @remove="handleRemoveDocument"
          @edit="handleEditDocument"
        />
      </transition>

      <!-- Kreditnotaer - skjult i AI mode hvis tom -->
      <transition name="category-fade">
        <DocumentCategory
          v-if="!isLiveMode || state.documents.kreditnoter.length > 0"
          category="kreditnoter"
          :title="$t('DEBTIA_CREDIT_NOTES')"
          icon="FileMinusIcon"
          variant="info"
          :documents="state.documents.kreditnoter"
          :is-test-mode="!isLiveMode"
          @analyze="handleAnalyzeDocument"
          @move="handleMoveDocument"
          @remove="handleRemoveDocument"
          @edit="handleEditDocument"
        />
      </transition>
    </div>

    <!-- Dokument Modal -->
    <DocumentEditModal
      v-if="editingDocument"
      :document="editingDocument"
      :categories="availableCategories"
      @save="handleSaveDocument"
      @close="handleCloseModal"
    />
  </div>
</template>

<script>
import {
  BProgress,
} from 'bootstrap-vue'
import { ref, computed, watch } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useCaseCreation } from '../composables/useCaseCreation'
import DocumentCategory from './DocumentCategory.vue'
import DocumentEditModal from './DocumentEditModal.vue'
import {
  analyzeDocumentWithClaude,
  claudeStatus,
  probeClaudeProxy,
} from '../services/claudeOcrService'

export default {
  name: 'StepGrundlag',

  components: {
    BProgress,
    DocumentCategory,
    DocumentEditModal,
  },

  setup() {
    const {
      state,
      addDocument,
      moveDocument,
      removeDocument,
      updateDocument,
      findDocumentById,
      setDocumentAiResult,
      validateDocumentForCategory,
      setAiMode,
      setAiAnalysisStatus,
      setAiExtractedDebtor,
      step1ValidationErrors,
    } = useCaseCreation()

    // Refs
    const fileInput = ref(null)
    const isDragging = ref(false)
    const editingDocument = ref(null)

    // AI Mode (computed for v-model)
    const isLiveMode = computed({
      get: () => state.aiMode === 'live',
      set: val => setAiMode(val ? 'live' : 'test'),
    })

    // Når proxyen ikke svarer, skal AI ikke kunne vælges. Uden dette bliver
    // brugeren ved med at få tilbudt en funktion, der fejler ved hver upload.
    const claudeUnavailable = computed(() => claudeStatus.value === 'unavailable')

    // Skifter automatisk til manuel, hvis AI falder ud undervejs.
    watch(claudeUnavailable, isUnavailable => {
      if (isUnavailable && state.aiMode === 'live') setAiMode('test')
    })

    // Billigt tjek af om nginx-proxyen er sat op. Ingen tokens brugt.
    probeClaudeProxy()

    // Accepted file types
    const acceptedFileTypes = '.pdf,.doc,.docx,.jpg,.jpeg,.png'

    // Refs
    const validationAlert = ref(null)

    // Check if any documents exist
    const hasAnyDocuments = computed(() => state.documents.grundlag.length > 0
      || state.documents.kreditnoter.length > 0
      || state.documents.rykkere.length > 0
      || state.documents.oevrig.length > 0)

    // Available categories for dropdown
    const availableCategories = computed(() => [
      { value: 'grundlag', label: i18n.t('DEBTIA_BASIS_INVOICES') },
      { value: 'rykkere', label: i18n.t('DEBTIA_REMINDERS') },
      { value: 'kreditnoter', label: i18n.t('DEBTIA_CREDIT_NOTES') },
      { value: 'oevrig', label: i18n.t('DEBTIA_OTHER_DOCUMENTATION') },
    ])

    // ============================================================
    // FILE HANDLING
    // ============================================================

    function triggerFileInput() {
      if (fileInput.value) {
        fileInput.value.click()
      }
    }

    function handleDragOver() {
      isDragging.value = true
    }

    function handleDragLeave() {
      isDragging.value = false
    }

    // ============================================================
    // AI ANALYSIS (defined first to avoid use-before-define)
    // ============================================================

    /* eslint-disable no-plusplus, no-await-in-loop */
    async function analyzeDocuments(docs) {
      // Initialiser scanning files til overlay (bruger state.aiAnalysis)
      const scanningFilesData = docs.map(doc => ({
        name: (doc.file && doc.file.name) || 'Ukendt',
        status: 'pending',
      }))

      setAiAnalysisStatus({
        isAnalyzing: true,
        progress: 0,
        totalCount: docs.length,
        completedCount: 0,
        errors: [],
        currentFileProgress: 0,
        scanningFiles: scanningFilesData,
      })

      for (let i = 0; i < docs.length; i++) {
        const doc = docs[i]

        // Marker nuværende fil som processing og reset progress for denne fil
        state.aiAnalysis.scanningFiles[i].status = 'processing'

        setAiAnalysisStatus({
          currentDocument: (doc.file && doc.file.name) || 'Ukendt',
          progress: Math.round(((i) / docs.length) * 100),
          currentFileProgress: 0,
        })

        try {
          const result = await analyzeDocumentWithClaude(doc.file, progress => {
            // Opdater progress for DENNE fil (0-100%)
            setAiAnalysisStatus({ currentFileProgress: progress })
          })

          if (result.success) {
            setDocumentAiResult(doc.id, result)

            // Auto-move to suggested category after successful AI analysis
            // This applies to both live AND manual mode - user triggered analysis should also categorize
            if (result.category) {
              const currentCategory = doc.category || 'oevrig'
              if (currentCategory !== result.category) {
                moveDocument(doc.id, currentCategory, result.category)
              }
            }

            // Gem AI-udtrukket debtor-info til brug i Step 2
            // Kun gem hvis der er debtor-data og vi ikke allerede har gemt noget
            if (result.debtor && !state.aiExtractedDebtor) {
              setAiExtractedDebtor(result.debtor)
            }

            // Marker fil som completed
            state.aiAnalysis.scanningFiles[i].status = 'completed'
          } else {
            setAiAnalysisStatus({
              errors: [...state.aiAnalysis.errors, { docId: doc.id, error: result.error }],
            })
            state.aiAnalysis.scanningFiles[i].status = 'error'
          }
        } catch (error) {
          console.error('AI analysis error:', error)
          setAiAnalysisStatus({
            errors: [...state.aiAnalysis.errors, { docId: doc.id, error: error.message }],
          })
          state.aiAnalysis.scanningFiles[i].status = 'error'
        }

        setAiAnalysisStatus({
          completedCount: i + 1,
          progress: Math.round(((i + 1) / docs.length) * 100),
        })
      }

      setAiAnalysisStatus({
        isAnalyzing: false,
        currentDocument: null,
      })

      // Ryd scanning files efter kort delay
      setTimeout(() => {
        setAiAnalysisStatus({ scanningFiles: [] })
      }, 500)
    }
    /* eslint-enable no-plusplus, no-await-in-loop */

    async function handleAnalyzeDocument(docId) {
      const doc = findDocumentById(docId)
      if (!doc || !doc.file) return

      await analyzeDocuments([doc])
    }

    // eslint-disable-next-line no-await-in-loop
    async function processFiles(files) {
      // Add all files to state
      const addedDocs = files.map(file => addDocument(file))

      // If live mode, auto-analyze
      if (isLiveMode.value) {
        await analyzeDocuments(addedDocs)
      }
    }

    // ============================================================
    // FILE HANDLING
    // ============================================================

    function handleDrop(event) {
      isDragging.value = false
      const files = Array.from(event.dataTransfer.files)
      processFiles(files)
    }

    function handleFileSelect(event) {
      const files = Array.from(event.target.files)
      processFiles(files)
      // Reset input
      // eslint-disable-next-line no-param-reassign
      event.target.value = ''
    }

    // ============================================================
    // DOCUMENT ACTIONS
    // ============================================================

    function handleMoveDocument(docId, fromCategory, toCategory) {
      moveDocument(docId, fromCategory, toCategory)
    }

    function handleRemoveDocument(docId) {
      removeDocument(docId)
    }

    function handleEditDocument(docId) {
      const doc = findDocumentById(docId)
      if (doc) {
        editingDocument.value = { ...doc }
      }
    }

    function handleSaveDocument(updatedDoc) {
      const doc = findDocumentById(updatedDoc.id)
      if (!doc) return

      const originalCategory = doc.category

      // Update all fields
      Object.keys(updatedDoc).forEach(key => {
        if (key !== 'id' && key !== 'file') {
          updateDocument(updatedDoc.id, key, updatedDoc[key])
        }
      })

      // Move to new category if changed
      if (updatedDoc.category !== originalCategory) {
        moveDocument(updatedDoc.id, originalCategory, updatedDoc.category)
      }

      // Re-validate the document after manual edits
      const targetCategory = updatedDoc.category || originalCategory
      updateDocument(updatedDoc.id, 'isValid', validateDocumentForCategory(doc, targetCategory))

      editingDocument.value = null
    }

    function handleCloseModal() {
      editingDocument.value = null
    }

    return {
      // State
      state,
      fileInput,
      isDragging,
      editingDocument,
      isLiveMode,
      claudeUnavailable,
      acceptedFileTypes,
      hasAnyDocuments,
      step1ValidationErrors,
      validationAlert,
      availableCategories,

      // Methods
      triggerFileInput,
      handleDragOver,
      handleDragLeave,
      handleDrop,
      handleFileSelect,
      handleAnalyzeDocument,
      handleMoveDocument,
      handleRemoveDocument,
      handleEditDocument,
      handleSaveDocument,
      handleCloseModal,
    }
  },
}
</script>

<style lang="scss" scoped>
// Debtia farver
$debtia-green: #088c76;
$debtia-blue: #002954;

// AI info hint
.ai-info-hint-wrapper {
  display: flex;
  justify-content: center;
}

.ai-info-hint {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  background: rgba(8, 140, 118, 0.06);
  color: $debtia-green;
  border: 1px solid rgba(8, 140, 118, 0.2);

  .ai-info-icon {
    flex-shrink: 0;
  }

  &.ai-info-hint-manual {
    background: rgba(130, 134, 139, 0.06);
    color: #6c757d;
    border-color: rgba(130, 134, 139, 0.2);
  }
}

// Main Drop Zone
.main-drop-zone {
  border: 2px dashed rgba(8, 140, 118, 0.35);
  border-radius: 0.75rem;
  padding: 2rem 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(8, 140, 118, 0.02);
  margin-bottom: 0.75rem;

  &:hover {
    background: rgba(8, 140, 118, 0.05);
    border-color: $debtia-green;
  }

  &.drop-zone-active {
    background: rgba(8, 140, 118, 0.1);
    border-color: $debtia-green;
    transform: scale(1.005);
  }

  .drop-zone-content {
    pointer-events: none;
  }

  .drop-zone-title {
    font-size: 0.9375rem;
    font-weight: 700;
    color: $debtia-green;
  }
}

// Mode switch (toggle)
.mode-switch-wrapper {
  display: flex;
  justify-content: center;
}

.mode-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: #e8e9eb;
  border-radius: 2rem;
  padding: 0.3rem;
  gap: 0;
  transition: all 0.35s ease;
  border: 2px solid transparent;

  // AI ON — festlig!
  &.mode-ai-on {
    background: linear-gradient(135deg, rgba(8, 140, 118, 0.12), rgba(52, 211, 153, 0.15));
    border-color: rgba(8, 140, 118, 0.3);
    box-shadow:
      0 0 16px rgba(8, 140, 118, 0.15),
      0 0 32px rgba(8, 140, 118, 0.08);

    .mode-switch-thumb {
      background: linear-gradient(135deg, $debtia-green, #34d399);
      box-shadow:
        0 2px 8px rgba(8, 140, 118, 0.4),
        0 0 20px rgba(8, 140, 118, 0.2);
    }
  }
}

.mode-switch-track {
  position: absolute;
  top: 0.3rem;
  left: 0.3rem;
  right: 0.3rem;
  bottom: 0.3rem;
  pointer-events: none;
}

.mode-switch-thumb {
  position: absolute;
  top: 0;
  left: 0;
  width: 50%;
  height: 100%;
  background: #b0b3b8;
  border-radius: 1.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &.thumb-right {
    transform: translateX(100%);
    background: #8b8fa3;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

.mode-switch-option {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.45rem 1.1rem;
  border: none;
  background: transparent;
  font-size: 0.8125rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  white-space: nowrap;
  outline: none;
  border-radius: 1.5rem;

  // Inactive = grå og kedelig
  color: #9a9da5;

  &.active {
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }

  &:not(.active):hover {
    color: #6c757d;
  }

  // AI-proxyen svarer ikke: knappen bliver nedtonet og kan ikke vælges,
  // så brugeren ledes mod manuel udfyldning i stedet.
  &.option-disabled {
    opacity: 0.4;
    cursor: not-allowed;

    &:hover {
      color: #9a9da5;
    }
  }
}

// Subtil puls-animation når AI er aktiv
.mode-ai-on {
  animation: aiGlow 2.5s ease-in-out infinite;
}

@keyframes aiGlow {
  0%, 100% {
    box-shadow:
      0 0 16px rgba(8, 140, 118, 0.15),
      0 0 32px rgba(8, 140, 118, 0.08);
  }
  50% {
    box-shadow:
      0 0 20px rgba(8, 140, 118, 0.22),
      0 0 40px rgba(8, 140, 118, 0.12);
  }
}

// Category fade transitions
.category-fade-enter-active,
.category-fade-leave-active {
  transition: all 0.3s ease;
}

.category-fade-enter,
.category-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

// AI Progress Section
.ai-progress-section {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
}

// Documents Grid
.documents-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

// Responsive
@media (max-width: 768px) {
  .main-drop-zone {
    padding: 1.5rem 1rem;
  }

  .mode-switch-option {
    padding: 0.35rem 0.85rem;
    font-size: 0.75rem;
  }
}

// ---- Dark Mode ----
.dark-layout {
  .ai-info-hint-wrapper .ai-info-hint {
    background: rgba(8, 140, 118, 0.1);
    border-color: rgba(8, 140, 118, 0.25);
    color: #34d399;

    &.ai-info-hint-manual {
      background: rgba(130, 134, 139, 0.08);
      color: #6b8299;
      border-color: rgba(130, 134, 139, 0.2);
    }
  }

  .mode-switch {
    background: #1a2d3e;
    border-color: transparent;

    &.mode-ai-on {
      background: linear-gradient(135deg, rgba(8, 140, 118, 0.18), rgba(52, 211, 153, 0.12));
      border-color: rgba(52, 211, 153, 0.3);
      box-shadow:
        0 0 20px rgba(8, 140, 118, 0.2),
        0 0 40px rgba(8, 140, 118, 0.1);

      .mode-switch-thumb {
        background: linear-gradient(135deg, $debtia-green, #34d399);
        box-shadow:
          0 2px 10px rgba(8, 140, 118, 0.5),
          0 0 24px rgba(8, 140, 118, 0.25);
      }
    }
  }

  .mode-switch-thumb {
    background: #3b5068;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

    &.thumb-right {
      background: #3b5068;
    }
  }

  .mode-switch-option {
    color: #4a6177;

    &.active {
      color: #fff;
    }

    &:not(.active):hover {
      color: #6b8299;
    }
  }

  .main-drop-zone {
    border-color: rgba(8, 140, 118, 0.4);
    background: rgba(8, 140, 118, 0.04);

    &:hover {
      background: rgba(8, 140, 118, 0.08);
    }

    &.drop-zone-active {
      background: rgba(8, 140, 118, 0.12);
    }

    .drop-zone-title {
      color: #34d399;
    }
  }

  .ai-progress-section {
    background: #0b1e32;
    border-color: #163350;
  }
}
</style>
